import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Sparkles, AlertTriangle, Loader2, Info, ClipboardList, CheckCircle2, Clock } from 'lucide-react';
import { useEncounter } from '@/contexts/EncounterContext';
import { emrService } from '@/services/emrService';
import { intakeService } from '@/services/intakeService';
import { cn } from '@/lib/utils';

/**
 * Pre-visit summary card surfaced at the top of the Overview tab. Calls the
 * (currently stubbed) `pre-visit-summary` endpoint, which returns either an
 * AI-synthesized summary or — until that ships — a deterministic fallback
 * built from active conditions / allergies / meds / prior visits.
 */
const PreVisitSummaryCard: React.FC = () => {
  const { clinicId, encounterId, encounter, appointmentId } = useEncounter();

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['encounter', encounterId, 'pre-visit-summary'],
    queryFn: () => emrService.getEncounterPreVisitSummary(clinicId, encounterId),
    staleTime: 60 * 1000,
    retry: 1,
    refetchOnWindowFocus: false,
  });

  // Pre-visit intake status — surfaced inline so the practitioner sees it the
  // moment they land on the encounter.
  const apptId = appointmentId || (encounter as any)?.appointment_id || null;
  const { data: intakeRes } = useQuery({
    queryKey: ['intakes', 'by-appointment', apptId],
    queryFn: () => intakeService.getIntakesForAppointment(apptId!),
    enabled: !!apptId,
    staleTime: 30 * 1000,
  });
  const intakes = (intakeRes?.data || []) as Array<{
    status: string; created_at: string; completed_at?: string | null;
  }>;

  if (isLoading) {
    return (
      <Card severity='normal'>
        <Header>
          <Loader2 className='h-3.5 w-3.5 animate-spin text-slate-400' />
          <span className='text-xs text-slate-500'>Loading pre-visit summary…</span>
        </Header>
      </Card>
    );
  }

  if (isError) {
    return (
      <Card severity='normal'>
        <Header>
          <Info className='h-3.5 w-3.5 text-slate-400' />
          <span className='text-xs text-slate-500'>
            Pre-visit summary unavailable.
            {error instanceof Error ? ` (${error.message})` : ''}
          </span>
        </Header>
      </Card>
    );
  }

  if (!data) return null;

  return (
    <Card severity={data.severity || 'normal'}>
      <Header>
        <Sparkles className='h-4 w-4 text-blue-500' />
        <span className='text-sm font-semibold text-slate-900'>Pre-visit summary</span>
        {data.stub && (
          <span className='ml-1 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium uppercase text-amber-700'>
            Preview
          </span>
        )}
      </Header>
      {data.severity === 'attention' && (
        <div className='mb-2 flex items-start gap-1 text-xs text-amber-700'>
          <AlertTriangle className='mt-0.5 h-3 w-3 shrink-0' />
          <span>Items below need a closer look before signing.</span>
        </div>
      )}
      <ul className='space-y-1.5 text-sm text-slate-700'>
        {data.bullets?.map((b, i) => (
          <li key={i} className='flex gap-2'>
            <span className='mt-1.5 h-1 w-1 shrink-0 rounded-full bg-slate-400' />
            <span>{b}</span>
          </li>
        ))}
      </ul>
      {data.message && (
        <p className='mt-3 border-t border-slate-100 pt-2 text-[11px] text-slate-500'>
          {data.message}
        </p>
      )}
      <IntakeStatusLine intakes={intakes} hasAppointment={!!apptId} />
    </Card>
  );
};

const IntakeStatusLine: React.FC<{
  intakes: Array<{ status: string; created_at: string; completed_at?: string | null }>;
  hasAppointment: boolean;
}> = ({ intakes, hasAppointment }) => {
  if (!hasAppointment) return null;

  const completed = intakes.find(i => i.status === 'completed');
  const pending = intakes.find(i => i.status === 'pending' || i.status === 'started');

  let icon: React.ReactNode;
  let label: string;
  let className = 'text-slate-700';

  if (completed) {
    icon = <CheckCircle2 className='h-3.5 w-3.5 text-emerald-600' />;
    label = `Intake completed${completed.completed_at ? ` ${new Date(completed.completed_at).toLocaleDateString()}` : ''}`;
    className = 'text-emerald-700';
  } else if (pending) {
    icon = <Clock className='h-3.5 w-3.5 text-amber-600' />;
    label = `Intake sent ${new Date(pending.created_at).toLocaleDateString()} — awaiting patient`;
    className = 'text-amber-700';
  } else if (intakes.length === 0) {
    icon = <ClipboardList className='h-3.5 w-3.5 text-slate-400' />;
    label = 'No pre-visit intake on file';
  } else {
    return null;
  }

  return (
    <div className={cn('mt-3 flex items-center gap-1.5 border-t border-slate-100 pt-2 text-xs', className)}>
      {icon}
      <span>{label}</span>
    </div>
  );
};

const Card: React.FC<{
  severity: 'normal' | 'attention' | 'critical';
  children: React.ReactNode;
}> = ({ severity, children }) => (
  <div
    className={cn(
      'rounded-lg border-l-4 border bg-white p-4',
      severity === 'critical'
        ? 'border-l-rose-500 border-rose-200'
        : severity === 'attention'
          ? 'border-l-amber-500 border-amber-200'
          : 'border-l-emerald-500 border-slate-200',
    )}
  >
    {children}
  </div>
);

const Header: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className='mb-2 flex items-center gap-2'>{children}</div>
);

export default PreVisitSummaryCard;
